/**
 * TEST SOMMEIL — le moteur de la modale.
 *
 * Les textes, les dimensions et les tranches de budget viennent du Liquid,
 * en JSON. Les matelas sont chargés à l'ouverture, depuis la collection
 * choisie dans l'éditeur (`?view=quiz`, voir templates/collection.quiz.liquid).
 * Rien n'est écrit en dur ici.
 *
 * Le calcul, question par question :
 *   - dimension : filtre. Seuls les matelas vendus et en stock dans cette
 *     dimension restent ;
 *   - budget : le prix DANS la dimension choisie. Les matelas dans la
 *     tranche passent devant ; s'il en manque, on complète avec les plus
 *     proches, marqués « hors budget » quand ils sont au-dessus ;
 *   - sensation : fermeté sur 10 lue par palier de 2. Même palier = 4
 *     points, palier voisin = 2, sinon 0. Un matelas sans note prend 2 :
 *     il n'est ni écarté ni mis en avant ;
 *   - à deux : +2 aux ressorts ensachés ;
 *   - chaud : +2 aux rafraîchissants.
 * À égalité, la sensation la plus proche passe devant, puis l'ordre de la
 * collection — celui que la boutique a choisi de mettre en avant.
 *
 * L'ouverture, le voile, Échap et le piège à focus viennent de <dialog>.
 */

const modale = document.querySelector('[data-quiz]');

if (modale) {
  let data = null;
  try {
    data = JSON.parse(modale.querySelector('[data-quiz-donnees]').textContent);
  } catch {
    /* Données illisibles : mieux vaut une modale qui ne s'ouvre pas qu'une
       modale vide. Les boutons gardent leur lien de repli. */
  }

  if (data?.tailles?.length) {
    const T = data.textes;
    const tete = modale.querySelector('[data-quiz-tete]');
    const scene = modale.querySelector('[data-quiz-scene]');
    const jauge = modale.querySelector('[data-quiz-jauge]');
    const compte = modale.querySelector('[data-quiz-compte]');
    const PALIER_NEUTRE = 3;

    /* Mode debug : `?quiz-debug` dans l'adresse affiche à côté du test le
       détail du calcul (assets/quiz-debug.js). Retenu pour la session,
       `?quiz-debug=0` l'éteint. Le fichier n'est chargé que dans ce mode :
       un visiteur ne le télécharge jamais. */
    let modeDebug = false;
    try {
      const drapeau = new URLSearchParams(window.location.search).get('quiz-debug');
      if (drapeau !== null) sessionStorage.setItem('quiz-debug', drapeau === '0' ? '' : '1');
      modeDebug = sessionStorage.getItem('quiz-debug') === '1';
    } catch {
      /* Stockage bloqué : pas de debug, le test marche quand même. */
    }
    const debug = (type) => {
      if (!modeDebug) return;
      modale.dispatchEvent(
        new CustomEvent('quiz:debug', { detail: { type, reponses: { ...reponses }, etapes, charger, classer } })
      );
    };
    if (modeDebug) {
      import(new URL('quiz-debug.js', import.meta.url).href)
        .then((module) => module.default(modale, data))
        .catch(() => console.warn('[quiz] debug indisponible'));
    }

    let catalogue = null;
    let rang = 0;
    let reponses = {};
    /* La case cliquée, par question. Deux réponses peuvent porter la même
       valeur (« Je ne sais pas » vise le palier 3, comme « Équilibre
       naturel ») : au retour, c'est la case qu'on rallume, pas la valeur. */
    let cases = {};

    const echapper = (s) =>
      String(s ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

    /* « 160 × 200 », « 160X200 » et « 160x200 » désignent la même taille :
       on compare la forme réduite. */
    const dimension = (s) =>
      String(s ?? '')
        .toLowerCase()
        .replace(/\s+/g, '')
        .replace(/×/g, 'x')
        .match(/\d+x\d+/)?.[0] ?? '';

    const palierDe = (note) => (note > 0 ? Math.ceil(note / 2) : 0);

    /* Les étapes. La question de budget saute si l'éditeur n'a aucune
       tranche : elle ne saurait rien filtrer. */
    const etapes = [
      {
        cle: 'taille',
        titre: T.qTaille,
        options: data.tailles.map((t) => ({ label: t, valeur: t })),
      },
      data.budgets.length && {
        cle: 'budget',
        titre: T.qBudget,
        options: data.budgets.map((b) => ({ label: b.label, valeur: b })),
      },
      {
        cle: 'palier',
        titre: T.qFermete,
        options: [
          ...data.paliers.map((nom, i) => ({ label: nom, valeur: i + 1 })),
          { label: T.nspNom, valeur: PALIER_NEUTRE },
        ],
      },
      {
        cle: 'deux',
        titre: T.qCouchage,
        options: [
          { label: T.seul, valeur: false },
          { label: T.deux, valeur: true },
        ],
      },
      {
        cle: 'chaud',
        titre: T.qChaleur,
        options: [
          { label: T.chaud, valeur: true },
          { label: T.pasChaud, valeur: false },
        ],
      },
    ].filter(Boolean);

    /* Le catalogue part dès l'ouverture : le temps de répondre aux
       questions, il est arrivé. Un échec n'est pas retenu, la prochaine
       ouverture réessaie.

       Pas d'en-tête `Accept: application/json` : Shopify y répond par son
       propre JSON de collection, sans passer par le gabarit `?view=quiz`. */
    function charger() {
      if (!catalogue) {
        catalogue = fetch(modale.dataset.catalogue)
          .then((r) => {
            if (!r.ok) throw new Error(r.status);
            return r.json();
          })
          .then((liste) => {
            if (!Array.isArray(liste)) throw new Error('catalogue illisible');
            return liste;
          })
          .catch((erreur) => {
            catalogue = null;
            throw erreur;
          });
      }
      return catalogue;
    }

    /* Rejoue l'animation d'entrée : on retire la classe, on force un
       recalcul de mise en page, on la remet. */
    const animer = () => {
      scene.classList.remove('qz-stage--in');
      void scene.offsetWidth;
      scene.classList.add('qz-stage--in');
    };

    /* Le titre de chaque étape prend le focus : un lecteur d'écran annonce
       la nouvelle question, le clavier repart du haut. */
    const focaliser = () => scene.querySelector('[data-quiz-titre]')?.focus({ preventScroll: true });

    const boutonRetour = () =>
      rang > 0
        ? `<div class="qz-foot"><button class="qz-back" type="button" data-quiz-retour>${echapper(T.retour)}</button></div>`
        : '';

    function rendre() {
      const etape = etapes[rang];
      tete.hidden = false;
      jauge.style.width = `${((rang + 1) / etapes.length) * 100}%`;
      compte.textContent = (T.compte || '').replace('[n]', rang + 1).replace('[total]', etapes.length);

      const choisi = cases[etape.cle];
      const n = etape.options.length;
      /* Deux colonnes dès 4 réponses : 7 dimensions empilées feraient
         défiler la fenêtre. Trois réponses s'empilent ; deux se tiennent
         côte à côte. */
      const grille = n >= 4 ? ' qz-opts--2' : n === 2 ? ' qz-opts--duo' : '';

      scene.innerHTML =
        `<h2 class="qz-q" tabindex="-1" data-quiz-titre>${echapper(etape.titre)}</h2>` +
        `<div class="qz-opts${grille}" role="group" aria-label="${echapper(etape.titre)}">` +
        etape.options
          .map(
            (o, i) =>
              `<button class="qz-opt" type="button" ` +
              `aria-pressed="${choisi === i}" data-quiz-choix="${i}">` +
              `<span>${echapper(o.label)}</span>` +
              '</button>'
          )
          .join('') +
        '</div>' +
        boutonRetour();

      scene.querySelectorAll('[data-quiz-choix]').forEach((b) => {
        b.addEventListener('click', () => {
          cases[etape.cle] = Number(b.dataset.quizChoix);
          reponses[etape.cle] = etape.options[cases[etape.cle]].valeur;
          scene
            .querySelectorAll('[data-quiz-choix]')
            .forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
          /* Un court délai : la sélection doit se voir avant qu'on passe. */
          setTimeout(avancer, 200);
        });
      });
      scene.querySelector('[data-quiz-retour]')?.addEventListener('click', () => {
        rang -= 1;
        rendre();
      });
      debug('etape');
      animer();
      focaliser();
    }

    function avancer() {
      if (rang < etapes.length - 1) {
        rang += 1;
        rendre();
      } else {
        resultat();
      }
    }

    function message(texte, lien) {
      tete.hidden = true;
      scene.innerHTML =
        '<div class="qz-msg">' +
        `<p class="qz-msg-txt" tabindex="-1" data-quiz-titre>${echapper(texte)}</p>` +
        (lien || '') +
        '</div>';
      animer();
      focaliser();
    }

    /* Le classement. Chaque matelas reçoit sa variante dans la dimension
       choisie (la moins chère en stock) ; sans elle, il sort du jeu. */
    function classer(produits) {
      const taille = dimension(reponses.taille);
      const budget = reponses.budget;
      const min = budget?.min ?? null;
      const max = budget?.max ?? null;
      const cible = reponses.palier ?? PALIER_NEUTRE;

      return produits
        .map((p, ordre) => {
          const variante = p.variantes
            .filter((v) => v.dispo && dimension(v.titre) === taille)
            .sort((a, b) => a.prix - b.prix)[0];
          if (!variante) return null;

          const euros = variante.prix / 100;
          const dessous = min !== null && euros < min;
          const dessus = max !== null && euros >= max;
          const ecart = dessous ? min - euros : dessus ? euros - max : 0;

          const palier = palierDe(p.fermete);
          const distance = palier ? Math.abs(palier - cible) : null;
          const points = {
            fermete: distance === null ? 2 : distance === 0 ? 4 : distance === 1 ? 2 : 0,
            ressorts: reponses.deux && p.ressorts ? 2 : 0,
            frais: reponses.chaud && p.frais ? 2 : 0,
          };
          const score = points.fermete + points.ressorts + points.frais;

          /* Sans note, la distance compte pour un palier et demi : derrière
             un voisin noté, devant un matelas franchement à côté. */
          const proximite = distance ?? 1.5;

          return { ...p, variante, palier, points, score, proximite, ordre, dansBudget: !budget || ecart === 0, dessus, ecart };
        })
        .filter(Boolean)
        .sort((a, b) => {
          if (a.dansBudget !== b.dansBudget) return a.dansBudget ? -1 : 1;
          /* Hors budget, la proximité du prix compte d'abord, par paliers de
             100 € : 20 € de trop valent mieux que 400 €, mais 20 € contre
             60 € ne doivent pas écraser l'adéquation au confort. */
          if (!a.dansBudget) {
            const pa = Math.ceil(a.ecart / 100);
            const pb = Math.ceil(b.ecart / 100);
            if (pa !== pb) return pa - pb;
          }
          /* À score égal, la sensation la plus proche passe devant : sans
             matelas au palier demandé, on propose le plus proche, pas le
             premier de la collection. */
          return b.score - a.score || a.proximite - b.proximite || a.ordre - b.ordre;
        });
    }

    const lienVariante = (p) => `${p.url}${p.url.includes('?') ? '&' : '?'}variant=${p.variante.id}`;

    const image = (p, largeur) =>
      p.image
        ? `<img src="${echapper(p.image)}" alt="${echapper(p.alt)}" width="${largeur}" height="${largeur}" loading="lazy" decoding="async">`
        : '';

    const prix = (p) =>
      `<p class="qz-res-price">${echapper(p.variante.prixTexte)}` +
      (p.variante.barre ? ` <s>${echapper(p.variante.barre)}</s>` : '') +
      '</p>' +
      (p.dessus ? `<p class="qz-res-over">${echapper(T.horsBudget)}</p>` : '');

    async function resultat() {
      message(T.chargement);
      let classement;
      try {
        classement = classer(await charger());
      } catch {
        message(T.erreur, `<button class="gm-btn gm-btn--cta" type="button" data-quiz-relancer>${echapper(T.recommencer)}</button>`);
        scene.querySelector('[data-quiz-relancer]').addEventListener('click', resultat);
        return;
      }
      /* La modale a pu être fermée pendant le chargement. */
      if (!modale.open) return;

      const [premier, ...autres] = classement.slice(0, 3);
      if (!premier) {
        message(
          T.vide,
          `<a class="gm-btn gm-btn--cta" href="${echapper(T.videUrl)}" data-quiz-sortie>${echapper(T.videCta)}</a>`
        );
        sortir();
        return;
      }

      const check =
        '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
        '<path d="m5 12.5 4.5 4.5L19 7" stroke="currentColor" stroke-width="1.7" ' +
        'stroke-linecap="round" stroke-linejoin="round"/></svg>';

      const atouts = [
        premier.palier && data.paliers[premier.palier - 1],
        reponses.deux && premier.ressorts && T.atoutRessorts,
        reponses.chaud && premier.frais && T.atoutFrais,
      ].filter(Boolean);

      const ligneDim = (T.resDim || '').replace('[dimension]', reponses.taille);

      tete.hidden = true;
      scene.innerHTML =
        '<div class="qz-res">' +
        `<h2 class="qz-kick" tabindex="-1" data-quiz-titre>${echapper(T.resKick)}</h2>` +
        '<article class="qz-res-main">' +
        `<div class="qz-res-ph">${image(premier, 480)}</div>` +
        '<div class="qz-res-body">' +
        `<h3 class="qz-res-name">${echapper(premier.nom)}</h3>` +
        `<p class="qz-res-dim">${echapper(ligneDim)}</p>` +
        prix(premier) +
        (atouts.length
          ? `<ul class="qz-res-ben">${atouts.map((a) => `<li>${check}<span>${echapper(a)}</span></li>`).join('')}</ul>`
          : '') +
        `<a class="gm-btn gm-btn--cta" href="${echapper(lienVariante(premier))}" data-quiz-sortie>` +
        `${echapper((T.resVoir || '').replace('[produit]', premier.nom))}</a>` +
        '</div></article>' +
        (autres.length
          ? `<h3 class="qz-res-alt-t">${echapper(T.resAutres)}</h3>` +
            '<ul class="qz-res-alt">' +
            autres
              .map(
                (p) =>
                  '<li><a class="qz-alt" href="' +
                  echapper(lienVariante(p)) +
                  '" data-quiz-sortie>' +
                  `<span class="qz-alt-ph">${image(p, 240)}</span>` +
                  `<span class="qz-alt-name">${echapper(p.nom)}</span>` +
                  (p.palier ? `<span class="qz-alt-tier">${echapper(data.paliers[p.palier - 1])}</span>` : '') +
                  `<span class="qz-alt-price">${echapper(p.variante.prixTexte)}</span>` +
                  (p.dessus ? `<span class="qz-res-over">${echapper(T.horsBudget)}</span>` : '') +
                  '</a></li>'
              )
              .join('') +
            '</ul>'
          : '') +
        '<div class="qz-res-actions">' +
        (T.resComparerUrl
          ? `<a class="gm-btn gm-btn--ghost" href="${echapper(T.resComparerUrl)}" data-quiz-sortie>${echapper(T.resComparer)}</a>`
          : '') +
        `<button class="qz-back" type="button" data-quiz-recommencer>${echapper(T.recommencer)}</button>` +
        '</div></div>';

      scene.querySelector('[data-quiz-recommencer]').addEventListener('click', demarrer);
      sortir();
      debug('resultat');
      animer();
      focaliser();
    }

    /* Les liens ferment la modale : sinon le verrou de défilement resterait
       posé sur la page qu'on vient d'ouvrir. */
    const sortir = () =>
      scene.querySelectorAll('[data-quiz-sortie]').forEach((a) => a.addEventListener('click', () => modale.close()));

    function demarrer() {
      rang = 0;
      reponses = {};
      cases = {};
      rendre();
    }

    /* Les boutons d'ouverture vivent dans les pages : la délégation sur le
       document les capte quelle que soit la page affichée. */
    document.addEventListener('click', (evenement) => {
      if (evenement.target.closest('[data-quiz-ouvrir]')) {
        evenement.preventDefault();
        charger().catch(() => {});
        modale.showModal();
        demarrer();
        return;
      }
      if (evenement.target.closest('[data-quiz-fermer]')) modale.close();
      else if (evenement.target === modale) modale.close();
    });
  }
}
